import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, CheckCircle2, CreditCard, FileMinus, FileText, Paperclip, Pencil, Plus, Printer, Trash2, UserPlus } from 'lucide-react';
import { Button, DataTable, FormField, Modal, StatusBadge } from '@/components/ui';
import { cn } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/axios';
import { toast } from '@/store/toastStore';
import type { StatusKey } from '@/types';
import { formatDZD } from '@/utils/algeria';
import { listChantiers } from '@/services/chantiers';
import { createTiers, listTiers, type TiersDTO } from '@/services/tiers';
import { uploadFile } from '@/services/uploads';
import {
  FACTURE_STATUTS,
  FACTURE_STATUT_LABEL,
  type ModePaiementFacture,
  MODES_PAIEMENT_FACTURE,
  MODE_PAIEMENT_FACTURE_LABEL,
  type PieceJointe,
  quotePartHtTva,
  TVA_TAUX_FACTURE,
  TYPE_DOC_LABEL,
  type TypeDocFacture,
} from '@/services/achats';
import {
  addEncaissement,
  creerAvoir,
  createFactureVente,
  deleteEncaissement,
  updateEncaissement,
  deleteFactureVente,
  type EncaissementDTO,
  type EncaissementInput,
  type FactureVenteDTO,
  type FactureVenteFiltres,
  type LigneFactureInputT,
  getFacturesVenteTotaux,
  listFacturesVente,
  openFactureCommercialePdf,
  openFactureVentePdf,
  STATUT_ENCAISSEMENT_LABEL,
  updateFactureVente,
  validerFactureVente,
} from '@/services/ventes';

const selectCls = 'flex h-10 w-full rounded-md border border-border bg-surface-secondary px-3 text-sm';
const ENC_UI: Record<string, StatusKey> = { SOLDEE: 'done', PARTIELLE: 'working', NON_ENCAISSEE: 'stuck' };

type LigneRow = { designation: string; unite: string; quantite: number; pu_ht: number; taux_tva: number; remise_pct: number };
const ligneVide = (): LigneRow => ({ designation: '', unite: '', quantite: 0, pu_ht: 0, taux_tva: 0.19, remise_pct: 0 });
const montantLigne = (l: LigneRow) => (Number(l.quantite) || 0) * (Number(l.pu_ht) || 0) * (1 - (Number(l.remise_pct) || 0));

interface FVForm {
  type_doc: string;
  comptabilisee: boolean;
  client_id: string;
  chantier_id: string;
  numero_facture: string;
  date_facture: string;
  date_echeance: string;
  montant_ht: number;
  tva_taux: string;
  tva_montant: number | '';
  montant_ttc: number | '';
  retenue_garantie_pct: number | '';
  mode_paiement: string;
  paiement_reference: string;
  paiement_banque: string;
  paiement_date: string;
  paiement_echeance: string;
  statut: string;
  date_paiement: string;
  notes: string;
}

const FORM_DEFAULTS: Partial<FVForm> = { type_doc: 'FACTURE', comptabilisee: false, tva_taux: '0.19', statut: 'BROUILLON', mode_paiement: '', tva_montant: '', montant_ttc: '', retenue_garantie_pct: '' };

export function FacturesVenteTab() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [detail, setDetail] = useState<FactureVenteDTO | null>(null);
  const [encFor, setEncFor] = useState<FactureVenteDTO | null>(null);
  const [pieces, setPieces] = useState<PieceJointe[]>([]);
  // Saisie « lignes d'abord » comme le devis : on démarre avec une ligne vide.
  const [lignes, setLignes] = useState<LigneRow[]>([ligneVide()]);
  const [q, setQ] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [filtres, setFiltres] = useState<FactureVenteFiltres>({});
  const setF = (patch: Partial<FactureVenteFiltres>) => setFiltres((f) => ({ ...f, ...patch }));

  const { data, isLoading } = useQuery({ queryKey: ['factures-vente', filtres], queryFn: () => listFacturesVente(filtres) });
  const { data: totaux } = useQuery({ queryKey: ['factures-vente-totaux', filtres], queryFn: () => getFacturesVenteTotaux(filtres) });
  const { data: clients } = useQuery({ queryKey: ['tiers', 'CLIENT'], queryFn: () => listTiers({ type: 'CLIENT', size: 300 }) });
  const { data: chantiersData } = useQuery({ queryKey: ['chantiers'], queryFn: () => listChantiers() });
  const clientItems = clients?.items ?? [];
  const clientsMap = new Map(clientItems.map((t) => [t.id, t.raison_sociale]));
  const chantierLabel = (id: string | null) => {
    if (!id) return null;
    const c = chantiersData?.items.find((x) => x.id === id);
    return c ? `${c.code} · ${c.nom}` : null;
  };

  const { register, handleSubmit, reset, setValue, watch } = useForm<FVForm>({ defaultValues: FORM_DEFAULTS });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['factures-vente'] });
    queryClient.invalidateQueries({ queryKey: ['factures-vente-totaux'] });
  };

  const htW = Number(watch('montant_ht')) || 0;
  const tauxSel = TVA_TAUX_FACTURE.find((t) => t.value === watch('tva_taux')) ?? TVA_TAUX_FACTURE[0];
  const tvaAuto = Math.round(htW * tauxSel.pct * 100) / 100;
  const ttcAuto = Math.round((htW + tvaAuto) * 100) / 100;

  // Création d'un client « à la volée ».
  const [creerCli, setCreerCli] = useState(false);
  const [nc, setNc] = useState({ raison_sociale: '', nif: '', nis: '', rc: '' });
  const creerCliMut = useMutation({
    mutationFn: () => {
      if (!nc.raison_sociale.trim()) return Promise.reject(new Error('La raison sociale est obligatoire.'));
      if (!nc.nif.trim() && !nc.rc.trim()) return Promise.reject(new Error('Renseignez au moins le NIF ou le RC (facture conforme).'));
      return createTiers({ code: '', type: 'CLIENT', raison_sociale: nc.raison_sociale.trim(), nif: nc.nif.trim() || null, nis: nc.nis.trim() || null, rc: nc.rc.trim() || null });
    },
    onSuccess: (t) => {
      // Ajoute le client au cache IMMÉDIATEMENT pour que l'<option> existe avant le
      // setValue (sinon le <select> rejette la valeur et le client n'est pas pris).
      queryClient.setQueryData(['tiers', 'CLIENT'], (old: { items?: TiersDTO[] } | undefined) => {
        const items = old?.items ?? [];
        return items.some((x) => x.id === t.id) ? old : { ...(old ?? { total: 0, page: 1, size: 300, pages: 1 }), items: [t, ...items] };
      });
      setValue('client_id', t.id, { shouldValidate: true, shouldDirty: true });
      queryClient.invalidateQueries({ queryKey: ['tiers', 'CLIENT'] });
      setCreerCli(false); setNc({ raison_sociale: '', nif: '', nis: '', rc: '' });
      toast.success(`Client « ${t.raison_sociale} » créé et sélectionné.`);
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const lignesValides = lignes.filter((l) => l.designation.trim());
  const detaille = lignesValides.length > 0;
  const totHt = lignesValides.reduce((s, l) => s + montantLigne(l), 0);
  const totTva = lignesValides.reduce((s, l) => s + montantLigne(l) * (Number(l.taux_tva) || 0), 0);

  const buildPayload = (v: FVForm) => {
    const ts = TVA_TAUX_FACTURE.find((t) => t.value === v.tva_taux) ?? TVA_TAUX_FACTURE[0];
    const lignesPayload: LigneFactureInputT[] | undefined = detaille
      ? lignesValides.map((l) => ({
          designation: l.designation.trim(), unite: l.unite || null,
          quantite: Number(l.quantite) || 0, pu_ht: Number(l.pu_ht) || 0,
          taux_tva: Number(l.taux_tva) || 0, remise_pct: Number(l.remise_pct) || 0,
        }))
      : undefined;
    return {
      type_doc: (v.type_doc || 'FACTURE') as TypeDocFacture,
      comptabilisee: !!v.comptabilisee,
      client_id: v.client_id || null,
      chantier_id: v.chantier_id || null,
      numero_facture: v.numero_facture || null,
      date_facture: v.date_facture || null,
      date_echeance: v.date_echeance || null,
      // En mode détaillé, les montants viennent des lignes (le serveur recalcule) ; sinon saisie libre.
      montant_ht: detaille ? Math.round(totHt * 100) / 100 : Number(v.montant_ht) || 0,
      tva_pct: ts.pct,
      tva_exonere: ts.exonere,
      tva_montant: detaille ? null : (v.tva_montant === '' ? null : Number(v.tva_montant)),
      montant_ttc: detaille ? null : (v.montant_ttc === '' ? null : Number(v.montant_ttc)),
      retenue_garantie_pct: v.retenue_garantie_pct === '' ? 0 : Number(v.retenue_garantie_pct) / 100,
      mode_paiement: (v.mode_paiement || null) as ModePaiementFacture | null,
      pieces,
      notes: v.notes || null,
      lignes: lignesPayload,
    };
  };

  const createMutation = useMutation({
    mutationFn: (v: FVForm) => createFactureVente(buildPayload(v)),
    onSuccess: () => { invalidate(); setOpen(false); reset(FORM_DEFAULTS); setPieces([]); setLignes([ligneVide()]); toast.success('Facture client enregistrée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const updateMutation = useMutation({
    mutationFn: (v: FVForm) => updateFactureVente(editId!, buildPayload(v)),
    onSuccess: () => { invalidate(); setOpen(false); setEditId(null); reset(FORM_DEFAULTS); setPieces([]); setLignes([ligneVide()]); toast.success('Facture modifiée.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const validerMutation = useMutation({ mutationFn: validerFactureVente, onSuccess: (f) => { invalidate(); toast.success(`Document validé : ${f.reference}.`); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const deleteMutation = useMutation({ mutationFn: deleteFactureVente, onSuccess: () => { invalidate(); toast.success('Facture supprimée.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });
  const avoirMutation = useMutation({
    mutationFn: (f: FactureVenteDTO) => creerAvoir(f.id, { motif: `Avoir sur ${f.reference || f.numero_facture || 'facture'}` }),
    onSuccess: () => { invalidate(); toast.success('Avoir créé (brouillon) — à valider pour le numéroter.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const openEdit = (f: FactureVenteDTO) => {
    const taux = f.tva_exonere
      ? (TVA_TAUX_FACTURE.find((t) => t.exonere)?.value ?? '0')
      : (TVA_TAUX_FACTURE.find((t) => !t.exonere && t.pct === f.tva_pct)?.value ?? '0.19');
    reset({
      type_doc: f.type_doc, comptabilisee: f.comptabilisee,
      client_id: f.client_id ?? '', chantier_id: f.chantier_id ?? '',
      numero_facture: f.numero_facture ?? '', date_facture: f.date_facture ?? '', date_echeance: f.date_echeance ?? '',
      montant_ht: f.montant_ht, tva_taux: taux,
      tva_montant: f.tva_exonere ? '' : f.tva_montant, montant_ttc: f.montant_ttc,
      retenue_garantie_pct: f.retenue_garantie_pct ? Math.round(f.retenue_garantie_pct * 1000) / 10 : '',
      statut: f.statut, notes: f.notes ?? '',
    });
    setPieces(f.pieces ?? []);
    const rows = (f.lignes ?? []).map((l) => ({
      designation: l.designation, unite: l.unite ?? '', quantite: Math.abs(Number(l.quantite)),
      pu_ht: Number(l.pu_ht), taux_tva: Number(l.taux_tva), remise_pct: Number(l.remise_pct),
    }));
    setLignes(rows.length ? rows : [ligneVide()]);
    setCreerCli(false);
    setEditId(f.id);
    setOpen(true);
  };

  const handleScan = async (file: File) => {
    try { const up = await uploadFile(file); setPieces((p) => [...p, { url: up.url, nom: up.filename }]); toast.success('Pièce jointe ajoutée.'); }
    catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const columns: ColumnDef<FactureVenteDTO, unknown>[] = [
    { accessorKey: 'numero_facture', header: 'N° facture', size: 116, cell: ({ getValue }) => (getValue() as string) ?? <span className="text-content-muted italic">—</span> },
    { accessorKey: 'type_doc', header: 'Type', size: 124, cell: ({ row }) => {
      const t = row.original.type_doc;
      const cls = t === 'SITUATION' ? 'bg-status-working/15 text-status-working'
        : t === 'AVOIR' ? 'bg-status-stuck/10 text-status-stuck' : 'bg-surface-hover text-content-secondary';
      return (
        <div className="flex flex-col gap-0.5">
          <span className={cn('inline-block w-fit rounded px-2 py-0.5 text-[11px] font-semibold', cls)}>{TYPE_DOC_LABEL[t]}</span>
          <span className="text-[10px] text-content-muted" title={row.original.origine === 'DEVIS' ? "Facture convertie depuis un devis" : "Facture saisie directement"}>{row.original.origine === 'DEVIS' ? 'issue d’un devis' : 'directe'}</span>
        </div>
      );
    } },
    { accessorKey: 'date_facture', header: 'Date', size: 92, cell: ({ getValue }) => (getValue() as string) ?? '—' },
    { id: 'client', header: 'Client', size: 160, cell: ({ row }) => clientsMap.get(row.original.client_id ?? '') ?? <span className="text-content-muted">—</span> },
    { accessorKey: 'montant_ht', header: 'HT', size: 96, cell: ({ getValue }) => <span className="tabular-nums">{formatDZD(getValue() as number)}</span> },
    { accessorKey: 'tva_montant', header: 'TVA', size: 92, cell: ({ row }) => row.original.tva_exonere ? <span className="text-[11px] text-content-muted">Exonéré</span> : <span className="tabular-nums">{formatDZD(row.original.tva_montant)}</span> },
    { accessorKey: 'montant_ttc', header: 'TTC', size: 100, cell: ({ getValue }) => <span className="tabular-nums font-medium">{formatDZD(getValue() as number)}</span> },
    { id: 'enc', header: 'Statut', size: 128, cell: ({ row }) => { const s = row.original.statut_encaissement; return <div className="flex flex-col gap-0.5"><StatusBadge status={ENC_UI[s]} label={STATUT_ENCAISSEMENT_LABEL[s]} />{s !== 'SOLDEE' && <span className="text-[10px] tabular-nums text-content-muted">reste {formatDZD(row.original.reste_a_encaisser)}</span>}</div>; } },
    { id: 'pj', header: 'Pièce', size: 80, cell: ({ row }) => { const has = (row.original.pieces?.length ?? 0) > 0; return has ? <span className="inline-flex items-center gap-1 text-status-done"><Paperclip className="size-4" />{(row.original.pieces?.length ?? 0) > 1 ? ` ${row.original.pieces.length}` : ''}</span> : <span className="inline-flex items-center gap-1 rounded bg-status-stuck/10 px-1.5 py-0.5 text-[10px] font-semibold text-status-stuck"><AlertTriangle className="size-3" /> manquante</span>; } },
    {
      id: 'actions', header: '', size: 296,
      cell: ({ row }) => {
        const f = row.original;
        return (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Imprimer le document officiel" onClick={() => openFactureCommercialePdf(f.id, f.reference ?? f.numero_facture, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Document officiel"><Printer className="size-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Fiche interne (encaissements / G50)" onClick={() => openFactureVentePdf(f.id, 'open').catch((e) => toast.error(getApiErrorMessage(e)))} aria-label="Fiche interne"><FileText className="size-4" /></Button>
            {f.statut_encaissement !== 'SOLDEE' && <Button variant="ghost" size="icon" className="h-8 w-8 text-brand-primary" onClick={() => setEncFor(f)} aria-label="Encaisser" title="Encaisser"><CreditCard className="size-4" /></Button>}
            {(f.statut === 'BROUILLON' || f.statut === 'LITIGE') && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-done" onClick={() => validerMutation.mutate(f.id)} aria-label="Valider"><CheckCircle2 className="size-4" /></Button>}
            {f.type_doc !== 'AVOIR' && <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" title="Créer un avoir (note de crédit)" onClick={() => { if (window.confirm('Créer un avoir (note de crédit) à partir de ce document ? Les lignes seront reprises en négatif.')) avoirMutation.mutate(f); }} aria-label="Créer un avoir"><FileMinus className="size-4" /></Button>}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(f)} aria-label="Modifier"><Pencil className="size-4" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-status-stuck" onClick={() => { if (window.confirm('Supprimer cette facture ?')) deleteMutation.mutate(f.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></Button>
          </div>
        );
      },
    },
  ];

  const periodG50 = !!(filtres.mois_encaissement && filtres.annee);

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
        <label className="flex flex-col text-xs text-content-muted" title="Filtre sur la date de la facture">Mois facture
          <select className={selectCls} value={filtres.mois ?? ''} onChange={(e) => setF({ mois: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Tous</option>
            {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, '0')}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted" title="Période de déclaration G50 : filtre les encaissements par leur mois de déclaration">Mois G50
          <select className={selectCls} value={filtres.mois_encaissement ?? ''} onChange={(e) => setF({ mois_encaissement: e.target.value ? Number(e.target.value) : null })}>
            <option value="">Tous</option>
            {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, '0')}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Année
          <input type="number" className={`${selectCls} w-24`} placeholder="Toutes" value={filtres.annee ?? ''} onChange={(e) => setF({ annee: e.target.value ? Number(e.target.value) : null })} />
        </label>
        <label className="flex flex-col text-xs text-content-muted">Client
          <select className={selectCls} value={filtres.client_id ?? ''} onChange={(e) => setF({ client_id: e.target.value || null })}>
            <option value="">Tous</option>
            {clientItems.map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Étape
          <select className={selectCls} value={filtres.statut ?? ''} onChange={(e) => setF({ statut: (e.target.value || null) as FactureVenteFiltres['statut'] })}>
            <option value="">Tous</option>
            {FACTURE_STATUTS.map((s) => <option key={s} value={s}>{FACTURE_STATUT_LABEL[s]}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Chantier
          <select className={selectCls} value={filtres.chantier_id ?? ''} onChange={(e) => setF({ chantier_id: e.target.value || null })}>
            <option value="">Tous</option>
            {(chantiersData?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Type
          <select className={selectCls} value={filtres.type_doc ?? ''} onChange={(e) => setF({ type_doc: (e.target.value || null) as FactureVenteFiltres['type_doc'] })}>
            <option value="">Tous</option>
            <option value="FACTURE">Facture</option>
            <option value="SITUATION">Situation de travaux</option>
            <option value="AVOIR">Avoir</option>
          </select>
        </label>
        <label className="flex flex-col text-xs text-content-muted">Taux TVA
          <select className={selectCls} value={filtres.tva_pct ?? ''} onChange={(e) => setF({ tva_pct: e.target.value === '' ? null : Number(e.target.value) })}>
            <option value="">Tous</option>
            <option value="0.19">19 %</option>
            <option value="0.09">9 %</option>
            <option value="0.07">7 %</option>
            <option value="0">0 % / Exonéré</option>
          </select>
        </label>
        {(filtres.mois || filtres.mois_encaissement || filtres.annee || filtres.client_id || filtres.statut || filtres.chantier_id || filtres.mode_paiement || filtres.type_doc || filtres.tva_pct != null) && (
          <Button variant="secondary" size="sm" onClick={() => setFiltres({})}>Réinitialiser</Button>
        )}
        <div className="ml-auto">
          <Button onClick={() => { reset(FORM_DEFAULTS); setPieces([]); setLignes([ligneVide()]); setCreerCli(false); setEditId(null); setOpen(true); }}><Plus /> Nouvelle facture</Button>
        </div>
      </div>

      {/* Totaux */}
      <div className={`grid grid-cols-2 gap-3 ${periodG50 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
        {[
          { l: 'Factures', v: String(totaux?.nombre ?? 0) },
          { l: 'Total HT', v: formatDZD(totaux?.total_ht ?? 0) },
          { l: 'Total TVA collectée', v: formatDZD(totaux?.total_tva ?? 0) },
          { l: 'Total TTC', v: formatDZD(totaux?.total_ttc ?? 0) },
          ...(periodG50 ? [{ l: `Encaissé · G50 ${String(filtres.mois_encaissement).padStart(2, '0')}/${filtres.annee}`, v: formatDZD(totaux?.total_encaisse_periode ?? 0) }] : []),
        ].map((k) => (
          <div key={k.l} className="rounded-lg border border-border bg-surface-secondary p-3 shadow-sm">
            <p className="text-xs text-content-muted">{k.l}</p>
            <p className="mt-0.5 text-lg font-bold tabular-nums text-content-primary">{k.v}</p>
          </div>
        ))}
      </div>

      <DataTable columns={columns} data={data?.items ?? []} globalFilter={q} onGlobalFilterChange={setQ} loading={isLoading} pageSize={15} exportFileName="factures-vente" emptyMessage="Aucune facture client." onRowClick={(f) => setDetail(f)} />

      {/* Création / édition */}
      <Modal
        open={open}
        onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}
        title={`${editId ? 'Modifier' : 'Nouveau document client'} — ${TYPE_DOC_LABEL[(watch('type_doc') || 'FACTURE') as TypeDocFacture]}`}
        footer={<><Button variant="secondary" onClick={() => { setOpen(false); setEditId(null); }}>Annuler</Button><Button onClick={editId ? handleSubmit((v) => updateMutation.mutate(v)) : handleSubmit((v) => createMutation.mutate(v))} loading={createMutation.isPending || updateMutation.isPending}>Enregistrer</Button></>}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField label="Type de document" hint="Facture, situation de travaux ou avoir — détermine l'écriture comptable (la situation → compte Travaux)."><select {...register('type_doc')} className={selectCls}><option value="FACTURE">Facture</option><option value="SITUATION">Situation de travaux</option><option value="AVOIR">Avoir (note de crédit)</option></select></FormField>
          <FormField label="N° de facture"><input {...register('numero_facture')} className={selectCls} placeholder="Ex. 2026/014" /></FormField>
          <FormField label="Client" className="sm:col-span-2">
            <div className="flex gap-2">
              <select {...register('client_id')} className={cn(selectCls, 'flex-1')}><option value="">— Sélectionner —</option>{clientItems.map((t) => <option key={t.id} value={t.id}>{t.raison_sociale}</option>)}</select>
              <Button type="button" variant="secondary" onClick={() => setCreerCli((v) => !v)}><UserPlus className="size-4" /> Créer</Button>
            </div>
          </FormField>
          {creerCli && (
            <div className="sm:col-span-2 rounded-md border border-border bg-surface-primary p-3">
              <p className="mb-2 text-xs text-content-muted">Nouveau client — renseignez au minimum le <strong>NIF ou le RC</strong>.</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input className={selectCls} placeholder="Raison sociale *" value={nc.raison_sociale} onChange={(e) => setNc({ ...nc, raison_sociale: e.target.value })} />
                <input className={selectCls} placeholder="NIF" value={nc.nif} onChange={(e) => setNc({ ...nc, nif: e.target.value })} />
                <input className={selectCls} placeholder="NIS" value={nc.nis} onChange={(e) => setNc({ ...nc, nis: e.target.value })} />
                <input className={selectCls} placeholder="RC" value={nc.rc} onChange={(e) => setNc({ ...nc, rc: e.target.value })} />
              </div>
              <div className="mt-2 flex justify-end"><Button type="button" size="sm" onClick={() => creerCliMut.mutate()} loading={creerCliMut.isPending}>Enregistrer le client</Button></div>
            </div>
          )}
          <FormField label="Date de facture"><input type="date" {...register('date_facture')} className={selectCls} /></FormField>
          <FormField label="Échéance"><input type="date" {...register('date_echeance')} className={selectCls} /></FormField>
          <FormField label="Chantier (optionnel)"><select {...register('chantier_id')} className={selectCls}><option value="">— Aucun —</option>{(chantiersData?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.nom}</option>)}</select></FormField>
          <FormField label="Retenue de garantie (%)"><input type="number" step="0.5" {...register('retenue_garantie_pct')} className={selectCls} placeholder="ex. 5" /></FormField>
          <FormField label="Mode de paiement"><select {...register('mode_paiement')} className={selectCls}><option value="">— Non précisé —</option>{MODES_PAIEMENT_FACTURE.filter((m) => m !== 'NON_PAYE').map((m) => <option key={m} value={m}>{MODE_PAIEMENT_FACTURE_LABEL[m]}</option>)}</select></FormField>

          {/* Lignes détaillées (document commercial officiel) */}
          <div className="sm:col-span-2">
            <div className="mb-1 flex items-center justify-between">
              <p className="text-xs uppercase tracking-wide text-content-muted">Lignes de la facture (désignation, qté, P.U. HT, TVA)</p>
              <Button type="button" variant="secondary" size="sm" onClick={() => setLignes((m) => [...m, ligneVide()])}><Plus className="size-4" /> Ligne</Button>
            </div>
            {lignes.length === 0 ? (
              <p className="rounded-md border border-dashed border-border bg-surface-primary px-3 py-2 text-xs text-content-muted">Aucune ligne : saisissez un montant global ci-dessous, ou <button type="button" className="font-medium text-brand-primary underline" onClick={() => setLignes([ligneVide()])}>ajoutez des lignes</button> (recommandé) — les montants sont alors calculés par le serveur.</p>
            ) : (
              <div className="rounded-md border border-border">
                <div className="grid grid-cols-12 gap-1 border-b border-border bg-surface-primary px-2 py-1.5 text-[11px] uppercase text-content-muted">
                  <div className="col-span-4">Désignation</div><div className="col-span-1">Unité</div><div className="col-span-2 text-right">Qté</div><div className="col-span-2 text-right">PU HT</div><div className="col-span-1 text-right">TVA</div><div className="col-span-1 text-right">Rem.%</div><div className="col-span-1" />
                </div>
                {lignes.map((l, i) => {
                  const upd = (patch: Partial<LigneRow>) => setLignes((m) => m.map((x, j) => (j === i ? { ...x, ...patch } : x)));
                  const ipt = 'h-8 rounded border border-border bg-surface-secondary px-2 text-sm';
                  return (
                    <div key={i} className="grid grid-cols-12 items-center gap-1 border-b border-border px-2 py-1 last:border-0">
                      <input className={cn(ipt, 'col-span-4')} value={l.designation} onChange={(e) => upd({ designation: e.target.value })} />
                      <input className={cn(ipt, 'col-span-1')} value={l.unite} onChange={(e) => upd({ unite: e.target.value })} />
                      <input type="number" step="1" className={cn(ipt, 'col-span-2 text-right tabular-nums')} value={l.quantite} onChange={(e) => upd({ quantite: Number(e.target.value) })} />
                      <input type="number" step="1" className={cn(ipt, 'col-span-2 text-right tabular-nums')} value={l.pu_ht} onChange={(e) => upd({ pu_ht: Number(e.target.value) })} />
                      <select className={cn(ipt, 'col-span-1')} value={String(l.taux_tva)} onChange={(e) => upd({ taux_tva: Number(e.target.value) })}><option value="0.19">19</option><option value="0.09">9</option><option value="0">0</option></select>
                      <input type="number" step="1" className={cn(ipt, 'col-span-1 text-right tabular-nums')} value={Math.round(l.remise_pct * 100)} onChange={(e) => upd({ remise_pct: (Number(e.target.value) || 0) / 100 })} />
                      <button type="button" className="col-span-1 flex justify-center text-status-stuck" onClick={() => setLignes((m) => m.filter((_, j) => j !== i))} aria-label="Retirer la ligne"><Trash2 className="size-4" /></button>
                    </div>
                  );
                })}
                <div className="flex justify-end gap-6 px-2 py-1.5 text-sm">
                  <span className="text-content-secondary">HT : <b className="tabular-nums text-content-primary">{formatDZD(totHt)}</b></span>
                  <span className="text-content-secondary">TVA : <b className="tabular-nums text-content-primary">{formatDZD(totTva)}</b></span>
                  <span className="text-content-secondary">TTC : <b className="tabular-nums text-content-primary">{formatDZD(totHt + totTva)}</b></span>
                </div>
              </div>
            )}
          </div>

          {lignes.length === 0 && (
            <>
              <FormField label="Montant HT"><input type="number" step="1" {...register('montant_ht')} className={selectCls} /></FormField>
              <FormField label="Taux TVA"><select {...register('tva_taux')} className={selectCls}>{TVA_TAUX_FACTURE.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></FormField>
              <FormField label={`Montant TVA (auto ${formatDZD(tvaAuto)})`}><input type="number" step="1" {...register('tva_montant')} className={selectCls} placeholder={String(tvaAuto)} /></FormField>
              <FormField label={`Montant TTC (auto ${formatDZD(ttcAuto)})`}><input type="number" step="1" {...register('montant_ttc')} className={selectCls} placeholder={String(ttcAuto)} /></FormField>
            </>
          )}
          <label className="flex items-center gap-2 text-sm text-content-secondary"><input type="checkbox" {...register('comptabilisee')} /> Comptabilisée</label>
          <FormField label="Observations" className="sm:col-span-2"><textarea {...register('notes')} className={cn(selectCls, 'h-20 py-2')} /></FormField>
          <div className="sm:col-span-2">
            <p className="mb-1 text-xs uppercase tracking-wide text-content-muted">Pièces jointes</p>
            <div className="flex flex-wrap items-center gap-2">
              {pieces.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-secondary px-2 py-1 text-xs">
                  <Paperclip className="size-3" /> {p.nom || `Pièce ${i + 1}`}
                  <button type="button" className="text-status-stuck" onClick={() => setPieces((ps) => ps.filter((_, j) => j !== i))}>×</button>
                </span>
              ))}
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleScan(f); e.target.value = ''; }} />
              <Button type="button" variant="secondary" size="sm" onClick={() => fileRef.current?.click()}><Paperclip className="size-4" /> Ajouter</Button>
            </div>
          </div>
        </div>
      </Modal>

      <EncaissementsModal facture={encFor} onClose={() => setEncFor(null)} onDone={invalidate} />
      <FactureVenteDetailModal
        facture={detail}
        clientNom={detail ? (clientsMap.get(detail.client_id ?? '') ?? null) : null}
        chantierNom={detail ? chantierLabel(detail.chantier_id) : null}
        onClose={() => setDetail(null)}
        onPrint={() => { if (detail) openFactureCommercialePdf(detail.id, detail.reference ?? detail.numero_facture, 'open').catch((e) => toast.error(getApiErrorMessage(e))); }}
        onAddEncaissement={() => { const d = detail; setDetail(null); if (d) setEncFor(d); }}
      />
    </div>
  );
}

/* ------------------------------- Encaissements ------------------------------ */
interface EncForm { date_encaissement: string; montant_encaisse: number | ''; periode_mois: number | ''; periode_annee: number | ''; mode_paiement: string; paiement_reference: string; paiement_banque: string; paiement_echeance: string; }

function EncaissementsModal({ facture, onClose, onDone }: { facture: FactureVenteDTO | null; onClose: () => void; onDone: () => void }) {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, watch } = useForm<EncForm>({ defaultValues: { mode_paiement: '' } });
  // Recharge la facture (encaissements à jour) tant que la modale est ouverte.
  const { data: f } = useQuery({
    queryKey: ['facture-vente', facture?.id],
    queryFn: async () => (await listFacturesVente({})).items.find((x) => x.id === facture!.id) ?? facture!,
    enabled: !!facture,
    initialData: facture ?? undefined,
  });
  const cur = f ?? facture;
  const refresh = () => { queryClient.invalidateQueries({ queryKey: ['facture-vente'] }); onDone(); };

  const mW = Number(watch('montant_encaisse')) || 0;
  const { ht: htL, tva: tvaL } = quotePartHtTva(mW, cur?.tva_pct ?? 0, cur?.tva_exonere);
  const modeW = watch('mode_paiement');
  const [editId, setEditId] = useState<string | null>(null);
  // Saisie : 'choix' (deux options), 'solder' (montant figé = reste), 'partiel' (montant libre ≤ reste).
  const [mode, setMode] = useState<'choix' | 'solder' | 'partiel'>('choix');
  useEffect(() => { setMode('choix'); setEditId(null); }, [facture]);
  const reste = Math.max(0, Number(cur?.reste_a_encaisser ?? 0));
  const depasse = !editId && mW > reste + 0.01;  // garde-fou UI (le serveur bloque aussi)
  const ouvrirSaisie = (m: 'solder' | 'partiel') => {
    setEditId(null); setMode(m);
    reset({ date_encaissement: new Date().toISOString().slice(0, 10), montant_encaisse: reste, mode_paiement: '', periode_mois: '', periode_annee: '', paiement_reference: '', paiement_banque: '', paiement_echeance: '' });
  };
  const startEdit = (e: EncaissementDTO) => {
    setEditId(e.id); setMode('partiel');
    reset({ date_encaissement: e.date_encaissement, montant_encaisse: e.montant_encaisse, periode_mois: e.periode_mois, periode_annee: e.periode_annee, mode_paiement: e.mode_paiement ?? '', paiement_reference: e.paiement_reference ?? '', paiement_banque: e.paiement_banque ?? '', paiement_echeance: e.paiement_echeance ?? '' });
  };

  const addMut = useMutation({
    mutationFn: (v: EncForm) => {
      const payload: EncaissementInput = {
        date_encaissement: v.date_encaissement,
        montant_encaisse: Number(v.montant_encaisse) || 0,
        periode_mois: v.periode_mois === '' ? null : Number(v.periode_mois),
        periode_annee: v.periode_annee === '' ? null : Number(v.periode_annee),
        mode_paiement: (v.mode_paiement || null) as ModePaiementFacture | null,
        paiement_reference: v.paiement_reference || null,
        paiement_banque: v.paiement_banque || null,
        paiement_echeance: v.paiement_echeance || null,
      };
      return editId ? updateEncaissement(editId, payload) : addEncaissement(cur!.id, payload);
    },
    onSuccess: () => { reset({ mode_paiement: '' }); setEditId(null); setMode('choix'); refresh(); toast.success(editId ? 'Encaissement modifié.' : 'Encaissement enregistré.'); },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });
  const delMut = useMutation({ mutationFn: deleteEncaissement, onSuccess: () => { refresh(); toast.success('Encaissement supprimé.'); }, onError: (e) => toast.error(getApiErrorMessage(e)) });

  return (
    <Modal open={!!facture} onOpenChange={(o) => { if (!o) onClose(); }} title={`Encaissements — facture ${cur?.numero_facture ?? ''}`} size="xl"
      footer={<Button variant="secondary" onClick={onClose}>Fermer</Button>}>
      {!cur ? null : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="rounded bg-surface-secondary px-2 py-1">TTC <strong className="tabular-nums">{formatDZD(cur.montant_ttc)}</strong></span>
            <span className="rounded bg-surface-secondary px-2 py-1">Encaissé <strong className="tabular-nums">{formatDZD(cur.total_encaisse)}</strong></span>
            {cur.retenue_garantie_montant > 0 && <span className="rounded bg-surface-secondary px-2 py-1 text-content-secondary" title="Retenue de garantie — non encaissée tant que le client ne l'a pas libérée">Retenu en garantie <strong className="tabular-nums">{formatDZD(cur.retenue_garantie_montant)}</strong></span>}
            <span className="rounded bg-surface-secondary px-2 py-1">Reste à encaisser <strong className="tabular-nums">{formatDZD(cur.reste_a_encaisser)}</strong></span>
            <StatusBadge status={ENC_UI[cur.statut_encaissement]} label={STATUT_ENCAISSEMENT_LABEL[cur.statut_encaissement]} />
          </div>

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase text-content-muted">
                <th className="px-3 py-2">Date</th><th className="px-3 py-2">G50</th><th className="px-3 py-2 text-right">Montant</th><th className="px-3 py-2 text-right">dont HT</th><th className="px-3 py-2 text-right">dont TVA</th><th className="px-3 py-2">Mode</th><th className="px-3 py-2">Réf.</th><th className="px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {cur.encaissements.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-3 text-center text-content-muted">Aucun encaissement.</td></tr>
                ) : cur.encaissements.map((e) => (
                  <tr key={e.id} className="border-b border-border/60 last:border-0">
                    <td className="px-3 py-1.5 tabular-nums">{e.date_encaissement}</td>
                    <td className="px-3 py-1.5 tabular-nums text-content-muted">{String(e.periode_mois).padStart(2, '0')}/{e.periode_annee}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatDZD(e.montant_encaisse)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(e.ht_encaisse)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(e.tva_encaisse)}</td>
                    <td className="px-3 py-1.5">{e.mode_paiement ? MODE_PAIEMENT_FACTURE_LABEL[e.mode_paiement] : '—'}</td>
                    <td className="px-3 py-1.5 text-content-secondary">{e.paiement_reference || '—'}</td>
                    <td className="px-3 py-1.5 text-right"><div className="flex justify-end gap-1"><button className="text-content-muted hover:text-content-primary" onClick={() => startEdit(e)} aria-label="Modifier"><Pencil className="size-4" /></button><button className="text-status-stuck" onClick={() => { if (window.confirm('Supprimer cet encaissement ?')) delMut.mutate(e.id); }} aria-label="Supprimer"><Trash2 className="size-4" /></button></div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Encaisser : deux options claires (Solder / Partiel) */}
          {cur.statut_encaissement === 'SOLDEE' && !editId ? (
            <div className="rounded-md bg-status-done/10 px-4 py-3 text-center text-sm font-semibold text-status-done">Facture soldée — aucun reste à encaisser.</div>
          ) : mode === 'choix' && !editId ? (
            <div className="rounded-md border border-border bg-surface-primary p-4">
              <p className="mb-3 text-sm font-semibold text-content-primary">Encaisser — reste à encaisser <span className="tabular-nums text-status-stuck">{formatDZD(reste)}</span></p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => ouvrirSaisie('solder')}><CheckCircle2 className="size-4" /> Solder ({formatDZD(reste)})</Button>
                <Button variant="secondary" onClick={() => ouvrirSaisie('partiel')}><CreditCard className="size-4" /> Encaissement partiel</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit((v) => addMut.mutate(v))} className="rounded-md border border-border bg-surface-primary p-3">
              <p className="mb-2 text-sm font-semibold">{editId ? 'Modifier l’encaissement' : mode === 'solder' ? `Solder la facture (${formatDZD(reste)})` : 'Encaissement partiel'}</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                <FormField label="Date d'encaissement" className="mb-0"><input type="date" required {...register('date_encaissement')} className={selectCls} /></FormField>
                <FormField label="G50 — mois" className="mb-0" hint="défaut = mois de l'encaissement"><input type="number" min="1" max="12" {...register('periode_mois')} className={selectCls} placeholder="auto" /></FormField>
                <FormField label="G50 — année" className="mb-0" hint="modifiable (retard de pièce)"><input type="number" {...register('periode_annee')} className={selectCls} placeholder="auto" /></FormField>
                <FormField label="Montant encaissé (TTC)" className="mb-0"><input type="number" step="1" required max={editId ? undefined : reste} readOnly={mode === 'solder' && !editId} {...register('montant_encaisse')} className={selectCls} /></FormField>
                <FormField label="Mode" className="mb-0"><select {...register('mode_paiement')} className={selectCls}><option value="">— Choisir —</option>{MODES_PAIEMENT_FACTURE.filter((m) => m !== 'NON_PAYE').map((m) => <option key={m} value={m}>{MODE_PAIEMENT_FACTURE_LABEL[m]}</option>)}</select></FormField>
                {modeW && <FormField label="Référence" className="mb-0"><input {...register('paiement_reference')} className={selectCls} /></FormField>}
                {modeW && <FormField label="Banque" className="mb-0"><input {...register('paiement_banque')} className={selectCls} /></FormField>}
              </div>
              {depasse && <p className="mt-2 text-xs font-semibold text-status-stuck">Le montant dépasse le reste à encaisser. Maximum autorisé : {formatDZD(reste)}.</p>}
              <p className="mt-2 text-xs text-content-muted">Décomposition : HT <strong className="tabular-nums">{formatDZD(htL)}</strong> · TVA collectée <strong className="tabular-nums">{formatDZD(tvaL)}</strong></p>
              <div className="mt-2 flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => { setEditId(null); setMode('choix'); reset({ mode_paiement: '' }); }}>Annuler</Button><Button type="submit" loading={addMut.isPending} disabled={depasse || mW <= 0}>{editId ? 'Enregistrer' : mode === 'solder' ? 'Confirmer le solde' : 'Enregistrer l’encaissement'}</Button></div>
            </form>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ---------------------------------- Détail ---------------------------------- */
function FactureVenteDetailModal({ facture, clientNom, chantierNom, onClose, onPrint, onAddEncaissement }: { facture: FactureVenteDTO | null; clientNom: string | null; chantierNom: string | null; onClose: () => void; onPrint: () => void; onAddEncaissement?: () => void }) {
  const f = facture;
  const taux = f?.tva_exonere ? 'Exonéré' : `${((f?.tva_pct ?? 0) * 100).toFixed(0)} %`;
  return (
    <Modal open={!!facture} onOpenChange={(o) => { if (!o) onClose(); }} title="Détail de la facture client" size="xl"
      footer={<><Button variant="secondary" onClick={onClose}>Fermer</Button><Button onClick={onPrint}><Printer className="size-4" /> Imprimer</Button></>}>
      {!f ? null : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-content-primary">{f.numero_facture || <span className="italic text-content-muted">Sans n°</span>}</span>
              <span className="rounded bg-surface-hover px-2 py-0.5 text-[11px] font-semibold uppercase text-content-muted">{TYPE_DOC_LABEL[f.type_doc]}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <StatusBadge status={ENC_UI[f.statut_encaissement]} label={STATUT_ENCAISSEMENT_LABEL[f.statut_encaissement]} />
            </div>
          </div>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-3">
            <Info label="Client">{clientNom}</Info>
            <Info label="Chantier">{chantierNom}</Info>
            <Info label="Retenue de garantie">{f.retenue_garantie_pct ? `${(f.retenue_garantie_pct * 100).toFixed(1)} % · ${formatDZD(f.retenue_garantie_montant)}` : '—'}</Info>
            <Info label="Date facture">{f.date_facture}</Info>
            <Info label="Échéance">{f.date_echeance}</Info>
            <Info label="Comptabilisée">{f.comptabilisee ? 'Oui' : 'Non'}</Info>
            <Info label="Validée le">{f.date_validation}</Info>
            <Info label="Référence interne">{f.reference}</Info>
            <Info label="Origine">{f.origine === 'DEVIS' ? "Convertie d'un devis" : 'Saisie directe'}</Info>
          </dl>
          <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-content-secondary">Total HT</span><span className="tabular-nums font-medium">{formatDZD(f.montant_ht)}</span></div>
            <div className="flex justify-between"><span className="text-content-secondary">TVA collectée ({taux})</span><span className="tabular-nums font-medium">{f.tva_exonere ? '—' : formatDZD(f.tva_montant)}</span></div>
            <div className="flex justify-between border-t border-border pt-1"><span className="font-bold text-content-primary">Total TTC</span><span className="tabular-nums font-bold text-brand-primary">{formatDZD(f.montant_ttc)}</span></div>
          </div>
          <div className="rounded-lg border border-border bg-surface-secondary p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-semibold text-content-primary">Encaissements (rattachement G50 par période de déclaration)</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-content-muted">Encaissé <strong className="tabular-nums text-content-primary">{formatDZD(f.total_encaisse)}</strong>{f.retenue_garantie_montant > 0 ? <> · Retenu en garantie <strong className="tabular-nums text-content-primary">{formatDZD(f.retenue_garantie_montant)}</strong></> : null} · Reste à encaisser <strong className="tabular-nums text-content-primary">{formatDZD(f.reste_a_encaisser)}</strong></span>
                {onAddEncaissement && f.statut_encaissement !== 'SOLDEE' && (
                  <Button size="sm" onClick={onAddEncaissement}><CreditCard className="size-4" /> Encaisser</Button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto rounded-md border border-border bg-surface-primary">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase text-content-muted"><th className="px-3 py-2">Date</th><th className="px-3 py-2">G50</th><th className="px-3 py-2 text-right">Montant</th><th className="px-3 py-2 text-right">dont HT</th><th className="px-3 py-2 text-right">dont TVA</th><th className="px-3 py-2">Mode</th><th className="px-3 py-2">Réf.</th></tr></thead>
                <tbody>
                  {f.encaissements.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-3 text-center text-content-muted">Aucun encaissement.</td></tr>
                  ) : f.encaissements.map((e) => (
                    <tr key={e.id} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-1.5 tabular-nums">{e.date_encaissement}</td>
                      <td className="px-3 py-1.5 tabular-nums text-content-muted">{String(e.periode_mois).padStart(2, '0')}/{e.periode_annee}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatDZD(e.montant_encaisse)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(e.ht_encaisse)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-content-secondary">{formatDZD(e.tva_encaisse)}</td>
                      <td className="px-3 py-1.5">{e.mode_paiement ? MODE_PAIEMENT_FACTURE_LABEL[e.mode_paiement] : '—'}</td>
                      <td className="px-3 py-1.5 text-content-secondary">{e.paiement_reference || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {(f.pieces?.length ?? 0) > 0 && (
            <div>
              <p className="mb-1.5 text-xs uppercase tracking-wide text-content-muted">Pièces jointes</p>
              <div className="flex flex-wrap gap-2">
                {f.pieces.map((pj, i) => (
                  <a key={i} href={pj.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-secondary px-2.5 py-1 text-sm text-brand-primary hover:bg-surface-hover"><Paperclip className="size-4" /> {pj.nom || `Pièce ${i + 1}`}</a>
                ))}
              </div>
            </div>
          )}
          {f.notes && (<div><p className="mb-1 text-xs uppercase tracking-wide text-content-muted">Observations</p><p className="whitespace-pre-wrap text-sm text-content-primary">{f.notes}</p></div>)}
        </div>
      )}
    </Modal>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs uppercase tracking-wide text-content-muted">{label}</dt>
      <dd className="text-sm text-content-primary">{children || '—'}</dd>
    </div>
  );
}
